import { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Ticket, Trophy, Target, Users, Heart, ChevronRight, DollarSign } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCAO_LOGO = process.env.REACT_APP_SCAO_LOGO_URL;

const addOns = [
  {
    icon: Gift,
    title: "Royal Woodbine Combo",
    price: "$80 / golfer",
    description: "The ultimate package for serious players",
    items: [
      "Breakfast Combo included",
      "2 mulligans (second chance shots)",
      "8 raffle tickets",
      "1 contest entry (longest drive or closest to pin)"
    ],
    highlight: true
  },
  {
    icon: Ticket,
    title: "50/50 Draw",
    price: "From $10",
    description: "Half goes to you, half goes to charity",
    items: [
      "Purchase tickets: $10 / $25 / $50 bundles",
      "Winner announced at dinner",
      "Previous jackpots over $2,000!"
    ]
  },
  {
    icon: Trophy,
    title: "Raffle Tickets",
    price: "$5 each",
    description: "Win amazing prizes from our sponsors",
    items: [
      "Golf equipment & apparel",
      "Local restaurant gift cards",
      "Spa & wellness packages",
      "Sports memorabilia"
    ]
  }
];

const contests = [
  {
    title: "Longest Drive",
    description: "Show off your power on the designated hole"
  },
  {
    title: "Closest to the Pin",
    description: "Test your accuracy on a par 3"
  },
  {
    title: "Putting Contest",
    description: "Sink the putt, win the prize"
  },
  {
    title: "Hole-in-One",
    description: "Win big prizes with a perfect shot"
  }
];

const donationAmounts = [10, 20, 25, 50, 100];

export default function FundraisingPage() {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [donating, setDonating] = useState(false);

  const handleDonate = async (amount) => {
    setSelectedAmount(amount);
    toast.success(`Selected $${amount} donation. Please send via e-transfer to bisngolf@tps.ca`);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F4] py-12 md:py-20">
      <div className="container-main">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-[#666055] mb-3">
            Maximize Your Impact
          </p>
          <h1 className="text-4xl md:text-5xl tracking-tight text-[#1C1C1C] mb-6" data-testid="fundraising-title">
            More Ways to Give
          </h1>
          <p className="text-lg text-[#666055] leading-relaxed">
            Every dollar raised supports the Sickle Cell Association of Ontario. 
            Add extras to your registration or participate in on-course activities 
            to boost your contribution and fun!
          </p>
        </div>

        {/* Direct Donation Section */}
        <section className="mb-16">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-[#E6E2D6] p-8 md:p-10">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-4">
                  <Heart size={32} className="text-[#C5A059]" />
                </div>
                <h2 className="text-3xl text-[#1C1C1C] mb-2">Make a Direct Donation</h2>
                <p className="text-[#666055] max-w-xl mx-auto">
                  Can't make it to the tournament? You can still support families affected by Sickle Cell Disease 
                  with a charitable donation.
                </p>
              </div>

              {/* Donation Amounts */}
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {donationAmounts.map((amount) => (
                  <Button
                    key={amount}
                    onClick={() => handleDonate(amount)}
                    variant={selectedAmount === amount ? "default" : "outline"}
                    className={`rounded-full px-6 py-3 text-lg font-medium transition-all ${
                      selectedAmount === amount
                        ? "bg-[#3A5F40] text-white"
                        : "border-[#3A5F40] text-[#3A5F40] hover:bg-[#3A5F40]/10"
                    }`}
                    data-testid={`donate-${amount}`}
                  >
                    ${amount}
                  </Button>
                ))}
              </div>

              {/* Tax Receipt Info */}
              <div className="bg-[#F9F8F4] rounded-xl p-4 text-center mb-4">
                <p className="text-sm text-[#666055]">
                  <span className="font-medium text-[#3A5F40]">Charitable Tax Receipt</span> will be provided 
                  for contributions over $20 (less 3% processing fee).
                </p>
              </div>

              {/* Payment Instructions */}
              <div className="text-center">
                <p className="text-xs text-[#666055] leading-relaxed">
                  Donations are accepted via e-transfer to{" "}
                  <a href="mailto:bisngolf@tps.ca" className="text-[#3A5F40] font-medium hover:underline">
                    bisngolf@tps.ca
                  </a>
                  . Should you require an alternative method of payment, please reach out to us directly at the above address and we will be pleased to accommodate.
                </p>
              </div>

              {/* Charity Logo - SCAO */}
              <div className="mt-6 pt-4 border-t border-[#E6E2D6] flex items-center justify-center gap-4">
                <img 
                  src={SCAO_LOGO}
                  alt="Sickle Cell Association of Ontario"
                  className="h-12 w-auto object-contain"
                />
                <div className="text-left">
                  <p className="text-xs text-[#666055]">Tax Receipts Issued By</p>
                  <p className="text-xs font-medium text-[#666055]">Sickle Cell Association of Ontario</p>
                  <p className="text-xs text-[#666055]">
                    Charitable Registration #: <span className="font-medium">890327067 RR0001</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Add-ons */}
        <section className="mb-16">
          <h2 className="text-3xl text-center mb-8 text-[#1C1C1C]">Add-On Packages</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {addOns.map((addon, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl border p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] ${
                  addon.highlight
                    ? "border-[#C5A059] ring-2 ring-[#C5A059]/20"
                    : "border-[#E6E2D6]"
                }`}
                data-testid={`addon-${index}`}
              >
                {addon.highlight && (
                  <span className="inline-block px-3 py-1 rounded-full bg-[#C5A059] text-white text-xs font-medium mb-4">
                    Best Value
                  </span>
                )}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#3A5F40]/10 flex items-center justify-center">
                    <addon.icon size={24} className="text-[#3A5F40]" />
                  </div>
                  <div>
                    <h3 className="text-xl text-[#1C1C1C]">{addon.title}</h3>
                    <p className="text-lg font-medium text-[#C5A059]">{addon.price}</p>
                  </div>
                </div>
                <p className="text-[#666055] mb-4">{addon.description}</p>
                <ul className="space-y-2">
                  {addon.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#666055]">
                      <span className="text-[#3A5F40] mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* On-Course Contests */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl border border-[#E6E2D6] p-8 md:p-12 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <Target size={40} className="mx-auto text-[#C5A059] mb-4" />
              <h2 className="text-3xl text-[#1C1C1C] mb-2">On-Course Contests</h2>
              <p className="text-[#666055]">
                Compete for prizes throughout your round
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {contests.map((contest, index) => (
                <div
                  key={index}
                  className="p-4 rounded-xl bg-[#F9F8F4] border border-[#E6E2D6]"
                  data-testid={`contest-${index}`}
                >
                  <h4 className="font-medium text-[#1C1C1C] mb-1">{contest.title}</h4>
                  <p className="text-sm text-[#666055]">{contest.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="mb-16">
          <div className="bg-[#2A4430] rounded-2xl p-8 md:p-12 text-white max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <Heart size={40} className="mx-auto text-[#C5A059] mb-4" />
              <h2 className="text-3xl mb-4">Where Your Donation Goes</h2>
              <p className="text-white/70 max-w-xl mx-auto">
                100% of net proceeds support the Sickle Cell Association of Ontario
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <Users size={28} className="text-[#C5A059]" />
                </div>
                <h4 className="font-medium mb-2">Patient Support</h4>
                <p className="text-sm text-white/60">
                  Direct assistance for families affected by sickle cell
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <Target size={28} className="text-[#C5A059]" />
                </div>
                <h4 className="font-medium mb-2">Research</h4>
                <p className="text-sm text-white/60">
                  Funding critical research for treatments and cures
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <Heart size={28} className="text-[#C5A059]" />
                </div>
                <h4 className="font-medium mb-2">Awareness</h4>
                <p className="text-sm text-white/60">
                  Education and community outreach programs
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/register"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="fundraising-register-btn"
          >
            Register & Add Extras
            <ChevronRight size={20} />
          </Link>
        </div>
      </div>
    </div>
  );
}
